import { isThisISOWeek, isThisYear, isToday, isYesterday } from 'date-fns';
import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { isNil } from 'lodash-es';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import { ChatMessageType } from '../../../proxies/ChatProxy';
import type { ChatMessageDataType } from '../../../redux/ducks/ChatDucks.types';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/EnvironmentHelper';
import Logo from '../../layout/Navbar/Logo';
import Spinner from '../../layout/Spinner/Spinner';
import AppointmentCancelDeniedMessage from './Message/AppointmentCancelDeniedMessage';
import AppointmentCancelRequestMessage from './Message/AppointmentCancelRequestMessage';
import AppointmentCanceledMessage from './Message/AppointmentCanceledMessage';
import AppointmentChangeDeniedMessage from './Message/AppointmentChangeDeniedMessage';
import AppointmentChangeRequestMessage from './Message/AppointmentChangeRequestMessage';
import AppointmentChangedMessage from './Message/AppointmentChangedMessage';
import AppointmentCompleteMessage from './Message/AppointmentCompleteMessage';
import AppointmentConfirmedMessage from './Message/AppointmentConfirmedMessage';
import AppointmentDeniedMessage from './Message/AppointmentDeniedMessage';
import AppointmentNewRequestMessage from './Message/AppointmentNewRequestMessage';
import PushEnableTipMessage from './Message/PushEnableTipMessage';
import TextMessage from './Message/TextMessage';

const MessageBubble: React.FC<ChatMessageDataType> = ({
  first,
  mine,
  system,
  previous,
  sending,
  user,
  data,
  disabled,
  provider,
}) => {
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const { currentUser } = useUser();
  const currentDay = Intl.DateTimeFormat(i18n.language, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: currentTimezone,
  }).format(new Date(data.timestamp));
  const prevDay = previous
    ? Intl.DateTimeFormat(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        timeZone: currentTimezone,
      }).format(new Date(previous.data.timestamp))
    : undefined;

  const dayChange =
    !isNil(first) && first ? true : prevDay ? currentDay !== prevDay : false;
  const { type, timestamp } = data;

  const formatDateChangeLabel = useCallback((ts: number) => {
    const messageDate = new Date(ts);

    if (isToday(messageDate)) {
      return t('static.generic.today');
    }
    if (isYesterday(messageDate)) {
      return t('static.generic.yesterday');
    }
    if (isThisISOWeek(messageDate)) {
      return t('static.generic.this_week_day', {
        name: Intl.DateTimeFormat(i18n.language, {
          weekday: 'long',
          timeZone: currentTimezone,
        }).format(messageDate),
      });
    }
    if (isThisYear(messageDate)) {
      return Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    }
    return Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: currentTimezone,
    }).format(new Date(messageDate));
  }, []);

  const generateMessageBody = () => {
    let parentStyleAddition =
      'text-sm border border-slate-200 bg-slate-100 drop-shadow-sm';

    if (type === ChatMessageType.Text) {
      if (mine) {
        parentStyleAddition =
          'text-sm border border-sky-900 bg-sky-800 text-white drop-shadow-sm';
      }
    } else if (!disabled) {
      if (
        type === ChatMessageType.NewAppointment ||
        type === ChatMessageType.AppointmentConfirmed ||
        type === ChatMessageType.AppointmentChanged
      ) {
        parentStyleAddition =
          'text-sm border border-green-200 bg-green-100 drop-shadow-sm';
      } else if (type === ChatMessageType.AppointmentCancelRequest) {
        parentStyleAddition =
          'text-sm border border-red-200 bg-red-100 drop-shadow-sm';
      } else if (type === ChatMessageType.AppointmentChangeRequest) {
        parentStyleAddition =
          'text-sm border border-yellow-200 bg-yellow-100 drop-shadow-sm';
      }
    }

    const avatar = mine
      ? undefined
      : user
        ? user?.media?.profile_picture
        : undefined;

    let messageComponent = <TextMessage data={data} user={user} mine={mine} />;

    if (type === ChatMessageType.NewAppointment) {
      messageComponent = (
        <AppointmentNewRequestMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
          disabled={disabled}
        />
      );
    }

    if (type === ChatMessageType.AppointmentConfirmed) {
      messageComponent = (
        <AppointmentConfirmedMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
          disabled={disabled}
        />
      );
    }

    if (type === ChatMessageType.AppointmentChangeRequest) {
      messageComponent = (
        <AppointmentChangeRequestMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
          disabled={disabled}
        />
      );
    }

    if (type === ChatMessageType.AppointmentCanceled) {
      messageComponent = (
        <AppointmentCanceledMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
        />
      );
    }

    if (type === ChatMessageType.AppointmentComplete) {
      messageComponent = (
        <AppointmentCompleteMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
        />
      );
    }

    if (type === ChatMessageType.AppointmentChanged) {
      messageComponent = (
        <AppointmentChangedMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
          disabled={disabled}
        />
      );
    }

    if (type === ChatMessageType.AppointmentDenied) {
      messageComponent = (
        <AppointmentDeniedMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
        />
      );
    }

    if (type === ChatMessageType.AppointmentChangeDenied) {
      messageComponent = (
        <AppointmentChangeDeniedMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
        />
      );
    }

    if (type === ChatMessageType.AppointmentCancelDenied) {
      messageComponent = (
        <AppointmentCancelDeniedMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
        />
      );
    }

    if (type === ChatMessageType.AppointmentCancelRequest) {
      messageComponent = (
        <AppointmentCancelRequestMessage
          data={data}
          user={user}
          mine={currentUser ? data?.user_id === currentUser.id : false}
          iamprovider={currentUser ? provider === currentUser.id : false}
          disabled={disabled}
        />
      );
    }

    if (type === ChatMessageType.PushEnableTip) {
      messageComponent = (
        <PushEnableTipMessage
          firstName={user ? user.first_name : ''}
          lastName={user ? user.last_name : ''}
        />
      );
    }

    if (mine) {
      return (
        <div
          className={`flex w-full grow flex-col border-solid p-3 shadow-sm ${
            mine
              ? 'rounded-s-xl rounded-ee-xl text-left'
              : 'rounded-e-xl rounded-es-xl text-left'
          } ${parentStyleAddition}`}
        >
          {messageComponent}
        </div>
      );
    }

    return (
      <span className="flex flex-row items-start justify-between gap-2">
        <span className="flex min-h-full flex-col items-start justify-start">
          <span className="relative flex size-[32px] flex-col items-center justify-center rounded-full bg-slate-300 text-center shadow-sm">
            <span className="flex size-full min-h-full flex-col items-center justify-center text-center text-xs font-bold capitalize">
              {!mine && user
                ? `${user?.first_name
                    .substring(0, 1)
                    .toUpperCase()}${user?.last_name
                    .substring(0, 1)
                    .toUpperCase()}`
                : ''}
            </span>
            {system ? (
              <span className="absolute left-0 top-0 size-[32px] rounded-full">
                <Logo size={16} showText={false} />
              </span>
            ) : avatar && user ? (
              <img
                alt={`${user?.first_name} ${user?.last_name}`}
                style={{
                  objectFit: 'cover',
                  minHeight: '100%',
                  minWidth: '100%',
                }}
                src={`//${getEnvironmentVariable(
                  'REACT_APP_CDN_API',
                )}${avatar}`}
                className="absolute left-0 top-0 size-[32px] rounded-full"
              />
            ) : null}
          </span>
        </span>
        <span>
          <div
            className={`flex w-full grow flex-col border-solid p-3 text-justify shadow-sm ${
              mine
                ? 'rounded-s-xl rounded-ee-xl text-right'
                : 'rounded-e-xl rounded-es-xl text-left'
            } ${parentStyleAddition}`}
          >
            {messageComponent}
          </div>
        </span>
      </span>
    );
  };

  return (
    <div
      className={`flex min-w-full flex-row ${
        !mine ? 'justify-start text-start' : 'justify-end text-end'
      } mt-4 py-2`}
      data-test-id={`chat-message-bubble-${data.id}`}
      key={`chat-message-bubble-${data.id}`}
    >
      <div
        className={`flex min-w-full flex-col ${
          mine ? 'items-end' : 'items-start'
        }`}
      >
        {dayChange && (
          <div className="my-6 flex min-h-[32px] min-w-full flex-col items-center justify-center">
            <div className="relative flex min-h-full min-w-full items-center py-5">
              <div className="min-h-full flex-1 border-t border-dashed border-slate-300">
                &nbsp;
              </div>
              <span className="mt-[-20px] flex min-h-full flex-col items-start justify-start bg-white px-3 text-sm capitalize text-slate-500">
                {formatDateChangeLabel(timestamp)}
              </span>
              <div className="min-h-full flex-1 border-t border-dashed border-slate-300">
                &nbsp;
              </div>
            </div>
          </div>
        )}
        <div
          className={`mx-2 flex min-w-[36px] max-w-full sm:max-w-[90%] md:max-w-[80%] lg:max-w-[65%] xl:max-w-[50%] ${
            !mine ? 'flex-row' : 'flex-row-reverse'
          }`}
        >
          <div className={`${sending ? 'opacity-25' : ''}`}>
            {generateMessageBody()}
          </div>
          <div
            className={`flex min-h-full min-w-[70px] flex-col ${
              mine ? 'items-end' : 'items-start'
            } justify-end px-2 text-xs text-slate-500`}
          >
            {sending ? (
              <Spinner color="fill-sky-600" scale="scale-50" />
            ) : (
              Intl.DateTimeFormat(i18n.language, {
                hour: 'numeric',
                minute: 'numeric',
                hour12: false,
                timeZone: currentTimezone,
              }).format(new Date(timestamp))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
